import { useNavigate } from 'react-router-dom';
import { Check, ChevronsUpDown, LayoutGrid } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ActivationPackWithTarget } from '@/types/activation/activation-pack';

interface PackGenieSwitcherProps {
  packs: ActivationPackWithTarget[];
  currentPackId: string;
  brand?: string;
}

const packLabel = (pack: ActivationPackWithTarget): string =>
  pack.agent?.name || pack.portal?.name || pack.name;

/**
 * Compact switcher for accounts with more than one pack. Replaces the old
 * "pick a Genie from a searchable list before you see anything" screen — the
 * full list still lives at /genie-hub/activation/all.
 */
export const PackGenieSwitcher = ({
  packs,
  currentPackId,
  brand = '#4E9CFF',
}: PackGenieSwitcherProps) => {
  const navigate = useNavigate();

  if (packs.length < 2) return null;

  const current = packs.find((pack) => pack.id === currentPackId);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex max-w-full items-center gap-2 rounded-full border bg-white px-3 py-1.5 text-xs font-medium shadow-sm transition-colors hover:bg-gray-50"
          style={{ borderColor: `${brand}55`, color: brand }}
        >
          <span className="truncate">
            {current ? packLabel(current) : 'Switch pack'}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 w-64 overflow-y-auto">
        <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Your packs
        </DropdownMenuLabel>
        {packs.map((pack) => {
          const isCurrent = pack.id === currentPackId;
          return (
            <DropdownMenuItem
              key={pack.id}
              onSelect={() => {
                if (isCurrent) return;
                navigate(`/genie-hub/activation/${pack.id}`);
              }}
              className="gap-2"
            >
              <Check
                className="h-4 w-4 shrink-0"
                style={{ color: brand, opacity: isCurrent ? 1 : 0 }}
              />
              <span className="truncate">{packLabel(pack)}</span>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => navigate('/genie-hub/activation/all')}
          className="gap-2"
        >
          <LayoutGrid className="h-4 w-4 shrink-0 text-gray-400" />
          View all packs
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
